// Small DOM helpers shared by the menu, the village and the raid, and the full-screen pages
// (Campaign, Multiplayer, Records) that open over the game with one big Back button.
import { bindKey } from './shortcuts.ts';
import { icon, type IconName } from './icons.ts';

export function el<K extends keyof HTMLElementTagNameMap>(tag: K, cls = '', text = ''): HTMLElementTagNameMap[K] {
  const e = document.createElement(tag);
  if (cls) e.className = cls;
  if (text) e.textContent = text;
  return e;
}

export interface ButtonOpts { cls?: string; disabled?: boolean; title?: string; active?: boolean; key?: string; icon?: IconName; sub?: string; /** An element to show as the picture instead of an icon. */ iconEl?: HTMLElement; /** Hide the label, keeping it for screen readers (an icon-only button). */ iconOnly?: boolean }

/** How a shortcut is written on its button. */
const KEY_LABEL: Record<string, string> = { Space: 'Space', Enter: '↵', Escape: 'Esc' };

/** A button: a label, optionally an icon or picture, a small line under it, and its shortcut in the corner. */
export function button(label: string, fn: () => void, opts: ButtonOpts = {}): HTMLButtonElement {
  const b = document.createElement('button');
  if (opts.icon || opts.sub || opts.iconEl) {
    if (opts.iconEl) b.appendChild(opts.iconEl);
    else if (opts.icon) b.appendChild(icon(opts.icon));
    if (!opts.iconOnly) b.appendChild(el('span', 'lbl', label));
    if (opts.sub) b.appendChild(el('span', 'sub', opts.sub));
  } else {
    b.textContent = label;
  }
  if (opts.key) b.appendChild(el('kbd', '', KEY_LABEL[opts.key] ?? opts.key));
  // the label, not the tooltip, is what screen readers should announce
  b.setAttribute('aria-label', label);
  b.onclick = fn;
  if (opts.cls) b.className = opts.cls;
  if (opts.disabled) b.disabled = true;
  if (opts.title) b.title = opts.title;
  if (opts.active) b.classList.add('active');
  if (opts.key) bindKey(b, opts.key);
  return b;
}

/** Stars as a row of filled and empty ones. */
export function stars(n: number, cls = 'stars'): HTMLSpanElement {
  const s = el('span', cls);
  s.append(el('span', '', '★'.repeat(n)), el('span', 'empty', '★'.repeat(Math.max(0, 3 - n))));
  s.setAttribute('aria-label', `${n} of 3 stars`);
  return s;
}

export interface Screen {
  /** Draws the page's body again, e.g. once its data has loaded. */
  refresh(): void;
  close(): void;
  readonly open: boolean;
}

const screens: Screen[] = [];

/** A full-screen page over the game: a title, a big Back button, and a body drawn by `render`. Escape goes back. */
export function openScreen(title: string, render: () => HTMLElement, onClose?: () => void): Screen {
  const wrap = el('div', 'screen');
  wrap.setAttribute('role', 'dialog');
  wrap.setAttribute('aria-label', title);
  const head = el('div', 'screen-head');
  const back = button('Back', () => screen.close(), { cls: 'back', title: 'Back (Esc)', icon: 'back' });
  head.append(back, el('h2', '', title));
  const body = el('div', 'screen-body');
  wrap.append(head, body);
  const onKey = (e: KeyboardEvent) => { if (e.key === 'Escape' && screens[screens.length - 1] === screen) { e.preventDefault(); e.stopImmediatePropagation(); screen.close(); } };
  let isOpen = true;
  const screen: Screen = {
    get open() { return isOpen; },
    refresh() { if (isOpen) body.replaceChildren(render()); },
    close() {
      if (!isOpen) return;
      isOpen = false;
      wrap.remove();
      screens.splice(screens.indexOf(screen), 1);
      document.removeEventListener('keydown', onKey, true);
      syncBody();
      onClose?.();
    },
  };
  document.addEventListener('keydown', onKey, true);
  screens.push(screen);
  document.body.appendChild(wrap);
  syncBody();
  screen.refresh();
  return screen;
}

/** Shuts every open page, e.g. when a fight starts. */
export function closeScreens(): void {
  for (const s of [...screens]) s.close();
}

/** While a page is up, the game's own buttons are hidden, so their shortcuts cannot fire behind it. */
function syncBody(): void {
  document.body.classList.toggle('screen-open', screens.length > 0);
}

let toastTimer = 0;
/** A short message in the middle of the screen, gone after a couple of seconds. */
export function toast(msg: string): void {
  let t = document.getElementById('toast');
  if (!t) { t = el('div'); t.id = 'toast'; t.setAttribute('role', 'status'); document.body.appendChild(t); }
  t.textContent = msg;
  t.classList.add('on');
  clearTimeout(toastTimer);
  toastTimer = window.setTimeout(() => t!.classList.remove('on'), 2200);
}
